import Auth from "@aws-amplify/auth";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { Temporal } from "temporal-polyfill";
import { z } from "zod";

import { MustReauthenticateError } from "./MustReauthenticateError";

interface TokenState {
  accessToken: string;
  expiresAt: Temporal.Instant;
}

type ExchangeErrorSource = "authx" | "cognito";
type ExchangeRedirectReason =
  | "authx_exchange_failed"
  | "cognito_session_failed";

let tokenState: TokenState | null = null;
let inflightPromise: Promise<string> | null = null;

const REFRESH_BUFFER = Temporal.Duration.from({ seconds: 30 });

const authxTokenResponseSchema = z.object({
  access_token: z.string().min(1),
  expires_in: z.number().int().positive(),
});

const computeExpiresAt = (expiresIn: number): Temporal.Instant =>
  Temporal.Now.instant().add(Temporal.Duration.from({ seconds: expiresIn }));

const isExpiringSoon = (expiresAt: Temporal.Instant): boolean =>
  Temporal.Instant.compare(
    expiresAt,
    Temporal.Now.instant().add(REFRESH_BUFFER),
  ) <= 0;

const getAuthxError = (data: unknown): string | undefined => {
  if (typeof data !== "object" || data === null || !("error" in data)) {
    return undefined;
  }

  const { error } = data;
  return typeof error === "string" ? error : undefined;
};

const getExchangeErrorSource = (err: unknown): ExchangeErrorSource =>
  axios.isAxiosError(err) || err instanceof z.ZodError ? "authx" : "cognito";

const getExchangeErrorMetadata = (err: unknown) => ({
  authxError: axios.isAxiosError(err)
    ? getAuthxError(err.response?.data)
    : undefined,
  code: axios.isAxiosError(err) ? err.code : undefined,
  source: getExchangeErrorSource(err),
  status: axios.isAxiosError(err) ? err.response?.status : undefined,
});

const getRedirectReason = (
  source: ExchangeErrorSource,
): ExchangeRedirectReason =>
  source === "authx" ? "authx_exchange_failed" : "cognito_session_failed";

const getSentryTitle = (source: ExchangeErrorSource): string =>
  source === "authx"
    ? "Authx token exchange failed"
    : "Cognito session unavailable for Authx token exchange";

const getSentryLevel = (source: ExchangeErrorSource): "info" | "warning" =>
  source === "authx" ? "warning" : "info";

const getExchangeErrorTags = (
  metadata: ReturnType<typeof getExchangeErrorMetadata>,
) => {
  const tags: Record<string, string | number> = {
    action: "exchange",
    component: "authxToken",
    redirectReason: getRedirectReason(metadata.source),
    source: metadata.source,
  };

  if (metadata.code) {
    tags.code = metadata.code;
  }

  if (metadata.status !== undefined) {
    tags.status = metadata.status;
  }

  return tags;
};

const captureExchangeError = (
  metadata: ReturnType<typeof getExchangeErrorMetadata>,
): void => {
  Sentry.captureException(new Error(getSentryTitle(metadata.source)), {
    level: getSentryLevel(metadata.source),
    tags: getExchangeErrorTags(metadata),
    extra: metadata,
  });
};

const toReauthenticationError = (err: unknown): MustReauthenticateError => {
  const metadata = getExchangeErrorMetadata(err);
  captureExchangeError(metadata);
  // Callers route this error through a hard login redirect, which discards
  // this module's in-memory token cache without extra local cleanup.
  return new MustReauthenticateError(
    getSentryTitle(metadata.source),
    getRedirectReason(metadata.source),
    metadata,
  );
};

const doExchange = async (): Promise<TokenState> => {
  const session = await Auth.currentSession();
  const cognitoToken = session.getIdToken().getJwtToken();

  const params = new URLSearchParams();
  params.append("grant_type", "urn:ietf:params:oauth:grant-type:jwt-bearer");
  params.append("assertion", cognitoToken);
  params.append("scope", "party");

  const res = await axios.post(
    `${window?.env?.AUTHX_API_URL}/oauth2/token`,
    params,
    { headers: { "Content-Type": "application/x-www-form-urlencoded" } },
  );

  const tokenResponse = authxTokenResponseSchema.parse(res.data);

  return {
    accessToken: tokenResponse.access_token,
    expiresAt: computeExpiresAt(tokenResponse.expires_in),
  };
};

export const getAccessToken = async (): Promise<string> => {
  if (tokenState && !isExpiringSoon(tokenState.expiresAt)) {
    return tokenState.accessToken;
  }

  if (inflightPromise) {
    return inflightPromise;
  }

  inflightPromise = doExchange()
    .then((state) => {
      tokenState = state;
      return state.accessToken;
    })
    .catch((err) => {
      throw toReauthenticationError(err);
    })
    .finally(() => {
      inflightPromise = null;
    });

  return inflightPromise;
};

export const getOptionalAccessToken = async (): Promise<string | undefined> => {
  try {
    await Auth.currentSession();
  } catch {
    return undefined;
  }

  try {
    return await getAccessToken();
  } catch {
    return undefined;
  }
};
